import { createFileRoute } from "@tanstack/react-router";
import downloadFile from "common/utils/downloadFile";
import { assertValidatesSchemaTODO } from "doc/schema/docSchemaAssertion";
import { useFetchImmutable } from "lib/fetcher";
import getPageTitle from "lib/utils/getPageTitle";
import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import useQueryParams from "ui/hooks/useQueryParams";
import withAuthentication from "../../components/withAuthentication";
import styles from "./compare-roof-viewer.module.scss";

enum CompareRoofsViewMode {
  BASELINE = "BASELINE",
  DIFF = "DIFF",
  TEST = "TEST",
}

interface TestHomeStatus {
  readonly totalFailures: number;
  readonly validatedFailures: number;
}

interface ShortcutConfig extends Partial<OptionalConfig> {
  code: KeyboardEvent["code"];
  shortcutTarget?: HTMLElement;
}

type OptionalConfig = Pick<KeyboardEvent, "altKey" | "ctrlKey" | "shiftKey">;
type ShortcutAction = (e: KeyboardEvent) => void;
type ValidatedTestFailures = { [key: string]: boolean };
type TestHomeStatuses = { [key: string]: TestHomeStatus };

const integrationBaseUrl = `/testResultsIntegration/tmpOutput/compareRoofs/TestPDFs`;
const promotionBaseUrl = `/testResultsPromotion/tmpOutput/compareRoofs/TestPDFs`;

const viewModeSuffix: { [key in CompareRoofsViewMode]: string } = {
  [CompareRoofsViewMode.BASELINE]: "-baseline",
  [CompareRoofsViewMode.DIFF]: "-diff",
  [CompareRoofsViewMode.TEST]: "",
};

function useKeyboardShortcut(shortcutAction: ShortcutAction, config: ShortcutConfig) {
  const eventHandler = useCallback(
    (e: Event) => {
      assertValidatesSchemaTODO<KeyboardEvent>(e);
      const { code, ctrlKey, altKey, shiftKey } = e;
      if (config.code !== code) {
        return;
      }
      if (config.ctrlKey && !ctrlKey) {
        return;
      }
      if (config.shiftKey && !shiftKey) {
        return;
      }
      if (config.altKey && !altKey) {
        return;
      }

      shortcutAction(e);
    },
    [shortcutAction, config],
  );

  useEffect(() => {
    const targetElement = config.shortcutTarget || document;
    targetElement.addEventListener("keydown", eventHandler);
    return () => targetElement.removeEventListener("keydown", eventHandler);
  }, [eventHandler]);
}

function CompareRoofViewer() {
  const [selectedFailureKey, setSelectedFailureKey] = useState<string>();
  const [leftViewMode, setLeftViewMode] = useState<CompareRoofsViewMode>(CompareRoofsViewMode.DIFF);
  const [rightViewMode, setRightViewMode] = useState<CompareRoofsViewMode>(CompareRoofsViewMode.TEST);
  const [validatedTestFailures, setValidatedTestFailures] = useState<ValidatedTestFailures>({});

  const { testType } = useQueryParams(["testType"]);
  const baseUrl = testType === "promotion" ? promotionBaseUrl : integrationBaseUrl;

  const indexUrl = `${baseUrl}/index.json`;
  const { data: testFailureIndex }: any = useFetchImmutable(indexUrl);

  const leftImageSrc = `${baseUrl}/${selectedFailureKey}${viewModeSuffix[leftViewMode]}.png`;
  const rightImageSrc = `${baseUrl}/${selectedFailureKey}${viewModeSuffix[rightViewMode]}.png`;
  const isTestFailureValidated = !!(selectedFailureKey && validatedTestFailures[selectedFailureKey]);
  let validatedOverlay: ReactNode = null;
  let imageOpacity = 1;
  if (isTestFailureValidated) {
    imageOpacity = 0.25;
    validatedOverlay = <div className={styles.validatedText}>Validated</div>;
  }
  /**
   * Load initial testFailure on index change
   */
  useEffect(() => {
    const keys = Object.keys(testFailureIndex ?? {});
    if (keys.length > 0) {
      setSelectedFailureKey(keys[0]);
    }
  }, [testFailureIndex]);

  // button handlers
  const setNext = () => {
    const keys = Object.keys(testFailureIndex ?? {});
    const currentIndex = keys.indexOf(selectedFailureKey ?? "");
    const nextIndex = (currentIndex + 1) % keys.length;
    setSelectedFailureKey(keys[nextIndex]);
  };

  const setPrev = () => {
    const keys = Object.keys(testFailureIndex ?? {});
    const currentIndex = keys.indexOf(selectedFailureKey ?? "");
    const prevIndex = (currentIndex - 1 + keys.length) % keys.length;
    setSelectedFailureKey(keys[prevIndex]);
  };

  const downloadMigrationIndex = async () => {
    // count total and validated failures for each test home
    const testHomeStatuses: TestHomeStatuses = {};
    for (const key in testFailureIndex ?? {}) {
      const testHomePath = testFailureIndex[key];
      if (!testHomeStatuses[testHomePath]) {
        testHomeStatuses[testHomePath] = {
          totalFailures: 0,
          validatedFailures: 0,
        };
      }

      const oldStatus = testHomeStatuses[testHomePath];

      testHomeStatuses[testHomePath] = {
        totalFailures: oldStatus.totalFailures + 1,
        validatedFailures: oldStatus.validatedFailures + (!!validatedTestFailures[key] ? 1 : 0),
      };
    }

    // migrate validated homes
    const migrationIndex: string[] = [];
    for (const testHomePath in testHomeStatuses) {
      const { totalFailures, validatedFailures } = testHomeStatuses[testHomePath];
      if (totalFailures > validatedFailures) {
        continue;
      }

      migrationIndex.push(testHomePath);
    }

    downloadFile("roofMigrationIndex.json", JSON.stringify(migrationIndex, undefined, 2), "application/json");
  };

  const toggleValidation = () => {
    if (isTestFailureValidated) {
      setValidatedTestFailures({
        ...validatedTestFailures,
        [selectedFailureKey]: false,
      });
    } else {
      setValidatedTestFailures({
        ...validatedTestFailures,
        [selectedFailureKey ?? ""]: true,
      });
    }
  };

  const toggleRightView = () => {
    if (rightViewMode === CompareRoofsViewMode.TEST) {
      setRightViewMode(CompareRoofsViewMode.BASELINE);
    } else {
      setRightViewMode(CompareRoofsViewMode.TEST);
    }
  };

  // shortcuts
  useKeyboardShortcut(setPrev, { code: "ArrowLeft" });
  useKeyboardShortcut(setNext, { code: "ArrowRight" });
  useKeyboardShortcut(toggleValidation, { code: "KeyV" });
  useKeyboardShortcut(toggleRightView, { code: "KeyC" });

  return (
    <>
      <Head>
        <title>{getPageTitle("Compare Roofs Viewer")}</title>
      </Head>
      <div id="images" style={{ display: "flex" }}>
        <div style={{ flex: "50%", margin: "0px" }}>
          <div className={styles.imageContainer}>
            <img
              id="left_image"
              src={leftImageSrc}
              style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain", opacity: imageOpacity }}
            />
            ${validatedOverlay}
          </div>
          <div style={{ margin: "auto", width: "20%" }}>
            <select
              value={leftViewMode}
              onChange={(e) => {
                setLeftViewMode(e.target.value as CompareRoofsViewMode);
              }}
              className={styles.compareRoofViewerSelect}
            >
              {Object.keys(CompareRoofsViewMode).map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div style={{ flex: "50%", margin: "0px" }}>
          <div className={styles.imageContainer}>
            <img
              id="right_image"
              src={rightImageSrc}
              style={{ maxHeight: "100%", maxWidth: "100%", objectFit: "contain", opacity: imageOpacity }}
            />
            ${validatedOverlay}
          </div>
          <div style={{ margin: "auto", width: "20%" }}>
            <select
              value={rightViewMode}
              onChange={(e) => {
                setRightViewMode(e.target.value as CompareRoofsViewMode);
              }}
              className={styles.compareRoofViewerSelect}
            >
              {Object.keys(CompareRoofsViewMode).map((key) => (
                <option key={key} value={key}>
                  {key}
                </option>
              ))}
            </select>
          </div>
          <div style={{ textAlign: "center" }}>Toggle between test and baseline using key C</div>
        </div>
      </div>
      <div className={styles.paramsContainer} style={{ marginTop: "75px" }}>
        <label className={styles.categoryModeLabel}>Change Sets:</label>
        <select // html select maintains focus after selection, so we can iterate test failures using arrows
          value={selectedFailureKey ?? ""}
          onChange={(e) => {
            setSelectedFailureKey(e.target.value);
          }}
          className={styles.compareRoofViewerSelect}
        >
          {Object.keys(testFailureIndex ?? {}).map((key) => (
            <option key={key} value={key}>
              {key}
            </option>
          ))}
        </select>
        <div>
          <button onClick={setPrev}>Prev [←]</button>
          <button onClick={setNext}>Next [→]</button>
        </div>
        <br />
        <div>
          <button onClick={toggleValidation}>{`${isTestFailureValidated ? "Invalidate" : "Validate"} [V]`}</button>
        </div>
        <br />
        <div>
          <button onClick={downloadMigrationIndex}>Download migration index</button>
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/compare-roof-viewer")({
  component: withAuthentication(CompareRoofViewer, { superAdmin: true }),
});
